import { initSingleLocationMap } from "../../features/single-location-map/index.js";

/**
 * @file Industry Single Map Module
 * @description Handles single industry location map functionality
 * @deprecated Use single-location-map feature instead
 */

export class IndustrySingleMap {
  constructor() {
    this.map = null;
    this.marker = null;
    this.boundary = null;
    this.isInitialized = false;
  }

  /**
   * Initialize the single industry map
   */
  async init() {
    const mapContainer = document.getElementById("industry-map");
    if (!mapContainer) {
      console.debug("Industry Single Map: map container not found, skipping initialization");
      return false;
    }

    // Get industry data from page
    const industryData = this.getIndustryData();
    if (!industryData) {
      console.warn("Industry Single Map: No industry data found");
      return false;
    }

    console.debug("Industry Single Map: Initializing map...");

    try {
      this.map = this.createMap(mapContainer);
      await this.initializeMapLogic(industryData);
      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error("Industry Single Map: Initialization failed:", error);
      return false;
    }
  }

  /**
   * Get industry data from page
   */
  getIndustryData() {
    const dataElement = document.getElementById("industry-data");
    if (!dataElement) return null;

    try {
      return JSON.parse(dataElement.textContent);
    } catch (error) {
      console.error("Error parsing industry data:", error);
      return null;
    }
  }

  /**
   * Create the Leaflet map instance
   */
  createMap(container) {
    const map = L.map(container, {
      scrollWheelZoom: true,
      zoomControl: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    return map;
  }

  /**
   * Initialize map logic with industry data
   */
  async initializeMapLogic(industryData) {
    const { lat, lng, name, boundary_data } = industryData;

    if (!lat || !lng) {
      showUserFriendlyNotyf(
        __("No coordinates available for this industrial park", "iphub"),
        "warning",
      );
      return;
    }

    // Set map center and zoom
    this.map.setView([lat, lng], 14);

    // Create and add marker
    this.marker = this.createIndustryMarker(industryData);
    this.marker.addTo(this.map);

    // Add boundary if available
    if (boundary_data) {
      this.boundary = this.createBoundary(boundary_data);
      this.boundary.addTo(this.map);

      // Fit map to boundary
      this.map.fitBounds(this.boundary.getBounds());
    } else {
      // Fit map to marker with padding
      this.map.fitBounds(this.marker.getBounds(), { padding: [20, 20] });
    }

    // Add click handler
    this.marker.on("click", () => {
      this.handleMarkerClick(industryData);
    });
  }

  /**
   * Create industry marker
   */
  createIndustryMarker(industryData) {
    const { lat, lng, name } = industryData;

    const icon = L.divIcon({
      html: `<div class="industry-marker">
        <div class="marker-icon">
          <img src="${this.getMarkerIcon()}" alt="Industry" />
        </div>
        <div class="marker-label">${name || "Industrial Park"}</div>
      </div>`,
      className: "industry-marker-container",
      iconSize: L.point(40, 40),
      iconAnchor: L.point(20, 40),
    });

    return L.marker([lat, lng], { icon });
  }

  /**
   * Get marker icon path
   */
  getMarkerIcon() {
    return "/wp-content/themes/iphub/img/factory.svg";
  }

  /**
   * Create boundary polygon
   */
  createBoundary(boundaryData) {
    try {
      const coordinates = this.parseBoundaryData(boundaryData);
      if (!coordinates || coordinates.length === 0) {
        return null;
      }

      return L.polygon(coordinates, {
        color: "#3B82F6",
        weight: 2,
        opacity: 0.8,
        fillColor: "#3B82F6",
        fillOpacity: 0.1,
      });
    } catch (error) {
      console.error("Error creating boundary:", error);
      return null;
    }
  }

  /**
   * Parse boundary data
   */
  parseBoundaryData(boundaryData) {
    if (typeof boundaryData === "string") {
      return JSON.parse(boundaryData);
    }
    return boundaryData;
  }

  /**
   * Handle marker click
   */
  handleMarkerClick(industryData) {
    // Show industry information
    this.showIndustryInfo(industryData);

    // Trigger custom event
    const event = new CustomEvent("industry-marker-click", {
      detail: { industryData },
    });
    document.dispatchEvent(event);
  }

  /**
   * Show industry information
   */
  showIndustryInfo(industryData) {
    const infoContainer = document.getElementById("industry-info");
    if (!infoContainer) return;

    const { name, description, contact_info, facilities } = industryData;

    infoContainer.innerHTML = `
      <div class="industry-info">
        <h3>${name || "Industrial Park"}</h3>
        ${description ? `<p class="description">${description}</p>` : ""}
        ${contact_info ? `<div class="contact-info">${contact_info}</div>` : ""}
        ${facilities ? `<div class="facilities">${facilities}</div>` : ""}
      </div>
    `;
  }

  /**
   * Reset map to initial state
   */
  resetMap() {
    if (!this.map || !this.marker) return;

    const markerLatLng = this.marker.getLatLng();
    this.map.setView(markerLatLng, 14);

    // Clear any existing info
    const infoContainer = document.getElementById("industry-info");
    if (infoContainer) {
      infoContainer.innerHTML = "";
    }
  }

  /**
   * Get current map state
   */
  getMapState() {
    if (!this.map) return null;

    return {
      center: this.map.getCenter(),
      zoom: this.map.getZoom(),
      bounds: this.map.getBounds(),
      hasMarker: !!this.marker,
      hasBoundary: !!this.boundary,
      isInitialized: this.isInitialized,
    };
  }

  /**
   * Destroy the map instance
   */
  destroy() {
    if (this.boundary) {
      this.map.removeLayer(this.boundary);
      this.boundary = null;
    }

    if (this.marker) {
      this.map.removeLayer(this.marker);
      this.marker = null;
    }

    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    this.isInitialized = false;
  }
}

/**
 * Initialize industry single map
 * @deprecated Use initSingleLocationMap from single-location-map feature instead
 */
export function initIndustrySingleMap() {
  // Use the new single-location-map feature
  return initSingleLocationMap();
}
